













































































































































import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {Order} from "@/model/Order";
import formatDate from "@/filters/DateFormatter";
import SupportPanel from "@/components/supports/SupportPanel.vue";
import {OrderProduct} from "@/model/OrderProduct";
import {Support, SupportStatuses} from "@/model/support/Support";
import {EventBus} from "@/helpers/EventBus";
import ProductLinePanel from "@/components/orders/ProductLinePanel.vue";
import ProductPreparation from "@/components/orders/ProductPreparation.vue";
import {Inject} from "vue-typedi";
import {SupportService} from "@/services/SupportService";
import {CreateSupportRequest} from "@/model/support/CreateSupportRequest";
import {OrderState} from "@/model/OrderState";
import {OrderService} from "@/services/OrderService";
import LoadingDialog from "@/components/dialog-box/LoadingDialog.vue";
import {ModifiedQuantity} from '@/model/order/ModifiedQuantity';
import {OrderedProductStatuses} from "@/model/OrderedProductStatus";
import {Quantity} from "@/model/Quantity";
import {SupportProduct} from "@/model/support/SupportProduct";
import {ConvertProductService} from "@/services/ConvertProductService";
import ErrorDialog from "@/components/dialog-box/ErrorDialog.vue";
import OutOfStockDialog from "@/components/dialog-box/OutOfStockDialog.vue";
import {ProductService} from "@/services/ProductService";
import {ConfirmOrderPreparationRequest} from "@/model/ConfirmOrderPreparationRequest";
import ScanDetector from "@/helpers/ScanDetector";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import {UpdateOrderRequirements} from "@/model/UpdateOrderRequirements";

@Component({
  components: {ConfirmDialog, OutOfStockDialog, ErrorDialog, SupportPanel, ProductLinePanel, ProductPreparation, LoadingDialog },
  filters: { formatDate },
})
export default class OrderPreparation extends Vue {
  @Prop({ default: {} }) order: Order;

  @Watch('order', {immediate: true}) watchOrderChange() { this.updateCurrentOrder(this.order); }

  @Inject()
  productService!: ProductService;
  @Inject()
  supportService!: SupportService;
  @Inject()
  orderService!: OrderService;
  @Inject()
  productConverter!: ConvertProductService;

  static readonly STORE_ID: string = "storeId";

  ERROR_MESSAGE_EXISTING_SUPPORT = "Le support scanné est déjà utilisé. Merci de scanner un nouveau support.";
  ERROR_MESSAGE_SUPPORT_CODE_INVALID = "Le numéro de support scanné est incorrect.";
  ERROR_MESSAGE_SUPPORT_DROPPED_OFF = "Le support scanné a déjà été déposé.";
  REGEX_SUPPORT_CODE = /^[A-Za-z0-9]{1,7}$/;

  currentOrder: Order;
  orphanProducts: OrderProduct[] = [];
  outOfStockProducts: OrderProduct[] = [];
  supports: Support[] = [];
  currentSupportCode: string | null = null;
  scanDetector: ScanDetector = new ScanDetector((value: string) => {
    this.currentSupportCode = value;
      this.loadSupport();
  });
  selectedSupport: Support = null;
  currentProduct: OrderProduct = null;
  currentSupportProduct: SupportProduct = null;
  canGoBack: boolean = true;
  isLoadingDialogOpen: boolean = false;
  isSendingInformationToWms: boolean = false;
  hasProcessOrderFailed: boolean = false;
  hasRetryProcessOrderFailed: boolean = false;
  hasScanError: boolean = false;
  errorMessage: string = '';
  isProcessedCommandDialogOpenWithOutOfStock: boolean = false;
  outOfStockProposition: string = '';
  displayOutOtStockProducts: boolean = false;
  currentStep: PreparationStep = PreparationStep.SUPPORT;
  isSubstitutionDialogOpen: boolean = false;
  forceOutOfStock: boolean = false;
  keyboardMode: string = 'none';
  foundQuantities: Map<string, Quantity> = new Map();
  isSupportEdited: boolean = false;
  isMissingSupportEdited: boolean = false;
  isApplyingSupportEdit: boolean = false;
  missingSupport: Support = new Support();
  persistQueue: Promise<void> = Promise.resolve();

  async updateCurrentOrder(order: Order) {
    this.currentOrder = order;
    await this.initSupports().then(supports => {
      this.supports = supports.filter(support => support.code != "MISSING");
      this.missingSupport = supports.find(support => support.code === "MISSING");
    });
    this.initOrphanProducts(order);
    this.initOutOfStockProducts(order);
    this.focusInput();
    EventBus.$on(EventBus.EDIT_SCANNABLE_PRODUCT,  this.onScannableProductEdit);
  }

  beforeDestroy() {
    this.scanDetector.reset();
    EventBus.$off(EventBus.EDIT_SCANNABLE_PRODUCT,  this.onScannableProductEdit);
  }

  initOrphanProducts(order: Order): void {
    const assignedProducts = new Set(
      this.supports.flatMap((support) =>
        (support.products ?? []).map((product) => product.cug)
      )
    );

    for (const product of this.missingSupport?.products ?? []) {
      assignedProducts.add(product.cug);
    }

    this.orphanProducts = (order?.products ?? []).filter(
      (product) => !assignedProducts.has(product.cug)
    );
  }

  initOutOfStockProducts(order: Order): void {
    if (!order?.products || !this.missingSupport?.products) {
      this.outOfStockProducts = [];
      return;
    }

    this.outOfStockProducts = this.missingSupport.products.flatMap((product) => {
      const orderProduct = this.getOrderProduct(product);

      if (!orderProduct) {
        return [];
      }

      orderProduct.status = OrderedProductStatuses.PREPARED;
      return [orderProduct];
    });
  }

  async initSupports(): Promise<Support[]> {
    return await this.supportService.getSupportsByOrderNumberAndZoneCode(this.currentOrder?.orderNumber, this.currentOrder.zone);
  }

  onScannableProductEdit(product: OrderProduct, supportProduct: SupportProduct): void {
    this.currentProduct = product;
    this.currentSupportProduct = supportProduct;
  }

  back(): void {
    if(this.isOrderProcessed) {
      EventBus.$emit(EventBus.PREPARE_ORDER, null);
    } else {
      EventBus.$emit(EventBus.CANCEL_ORDER_PREPARATION, this.currentOrder);
    }
  }

  loadSupport(): void {
    if (!this.hasValidCurrentSupportCode) {
      this.showScanError(this.ERROR_MESSAGE_SUPPORT_CODE_INVALID);
      return;
    }

    const existingSupport = this.findSupportByCode(this.currentSupportCode.trim());

    if (existingSupport != null) {
      if(existingSupport.supportStatus === SupportStatuses.DROPPED_OFF) {
        this.showScanError(this.ERROR_MESSAGE_SUPPORT_DROPPED_OFF);
        return;
      }
      this.selectedSupport = existingSupport;
      this.cleanEmptySupports();
      return;
    }

    this.fetchOrCreateSupport();
  }

  findSupportByCode(code: string): Support | undefined {
    return this.supports.find(s => s.code === code);
  }

  getOrderProduct(supportProduct: SupportProduct): OrderProduct {
    return this.currentOrder.products.find(product => product.cug === supportProduct.cug);
  }

  async fetchOrCreateSupport() {
    try {
      const existsSupport = await this.supportService.existsSupport(this.currentSupportCode.trim());

      if (existsSupport) {
        this.showScanError(this.ERROR_MESSAGE_EXISTING_SUPPORT);
        return;
      }

      this.cleanEmptySupports();

      const newSupport = this.createSupport();
      this.selectedSupport = newSupport;
      this.supports.push(newSupport);
    } catch (error: any) {
      this.showScanError(error.message);
    }
  }

  showScanError(message: string) {
    this.hasScanError = true;
    this.errorMessage = message;
  }

  createSupport(): Support {
    const newSupport = new Support();
    newSupport.code = this.currentSupportCode.trim();
    newSupport.storeId = sessionStorage.getItem(OrderPreparation.STORE_ID);
    newSupport.zoneCode = this.currentOrder.zone;
    newSupport.orderNumber = this.currentOrder.orderNumber;
    newSupport.driveId = this.currentOrder.driveId;
    newSupport.deliveryType = this.currentOrder.type;
    newSupport.plannedSlot = this.currentOrder.pickupDate;
    newSupport.collectionSlot = this.currentOrder.pickupDate;
    return newSupport;
  }

  async endOrderPreparation() {
    this.isLoadingDialogOpen = true;
    this.isSendingInformationToWms = true;
    this.isSupportEdited = false;
    this.isMissingSupportEdited = false;
    try {

      if (this.currentStep === PreparationStep.SUPPORT) {
        await this.prepareSupportForCollection();
        this.currentStep = PreparationStep.ORDER;
      }

      if (this.currentStep === PreparationStep.ORDER) {
        await this.processOrder();
        this.currentStep = PreparationStep.TALEND;
      }

      if (this.currentStep === PreparationStep.TALEND) {
        await this.sendOrderToTalend();
        this.currentStep = PreparationStep.DONE;
      }

      this.isLoadingDialogOpen = false;
      this.isSendingInformationToWms = false;

      this.back();
    } catch (e) {
      this.isSendingInformationToWms = false;

      // For Talend error, we just retry once and then ignore the error.
      if (this.hasProcessOrderFailed && this.currentStep === PreparationStep.TALEND) {
        this.isLoadingDialogOpen = false;
        this.canGoBack = true;
        return;
      }

      if (this.hasProcessOrderFailed) {
        this.hasRetryProcessOrderFailed = true;
        this.canGoBack = true;
      }

      this.hasProcessOrderFailed = true;
    }

  }

  async updateOrderPreparation() {
    this.isLoadingDialogOpen = true;
    this.isSendingInformationToWms = true;
    this.isSupportEdited = false;
    this.isMissingSupportEdited = false;
    try {

      if (this.currentStep === PreparationStep.SUPPORT) {
        await this.prepareSupportForCollection();
        this.currentStep = PreparationStep.ORDER;
      }

      if (this.currentStep === PreparationStep.ORDER) {
        await this.updateOrder();
        this.currentStep = PreparationStep.TALEND;
      }

      if (this.currentStep === PreparationStep.TALEND) {
        await this.sendOrderToTalend();
        this.currentStep = PreparationStep.DONE;
      }

      this.isLoadingDialogOpen = false;
      this.isSendingInformationToWms = false;

      this.back();
    } catch (e) {
      this.isSendingInformationToWms = false;

      // For Talend error, we just retry once and then ignore the error.
      if (this.hasProcessOrderFailed && this.currentStep === PreparationStep.TALEND) {
        this.isLoadingDialogOpen = false;
        this.canGoBack = true;
        return;
      }

      if (this.hasProcessOrderFailed) {
        this.hasRetryProcessOrderFailed = true;
        this.canGoBack = true;
      }

      this.hasProcessOrderFailed = true;
    }

  }

  createSupportRequestFromSupport(support: Support): CreateSupportRequest {
    const createSupportRequest = new CreateSupportRequest();
    createSupportRequest.orderNumber = support.orderNumber;
    createSupportRequest.zoneCode = support.zoneCode;
    createSupportRequest.driveId = support.driveId;
    createSupportRequest.deliveryType = support.deliveryType;
    createSupportRequest.storeId = support.storeId;
    createSupportRequest.plannedSlot = support.plannedSlot;
    createSupportRequest.products = support.products;
    createSupportRequest.supportCode = support.code;
    createSupportRequest.collectionSlot = support.collectionSlot;
    return createSupportRequest;
  }

  async prepareSupportForCollection() {
    const promises = this.supports.filter(support => support.code != "MISSING" && support.supportStatus === SupportStatuses.CREATED)
      .map(support => this.supportService.prepareSupportForCollection(support.code));

    const results = await Promise.allSettled(promises);

    if (results.some(r => r.status === 'rejected')) {
      throw new Error('Support creation failed');
    }
  }

  async processOrder(): Promise<Order[]> {
    const res = await this.orderService.changeOrderState(
      this.currentOrder.orderNumber,
      this.currentOrder.zone,
      OrderState.PROCESSED,
      this.currentOrder.products
    );

    if (res == null || res.length === 0) {
      throw new Error('Change order state failed.');
    }

    this.currentOrder = res.find(order => order.orderId === this.currentOrder.orderId);

    this.processUpdateOrderRequirements();

    for (const order of res) {
      EventBus.$emit(EventBus.CHANGE_ORDER_STATE, order);
    }

    this.canGoBack = true;

    return res;
  }

  processUpdateOrderRequirements(): void {
    const hasMissingProducts = (this.missingSupport?.products?.length ?? 0) > 0;
    let requirements = this.currentOrder.updateOrderRequirements;

    if (!requirements) {
      requirements = new UpdateOrderRequirements(hasMissingProducts, true);
      this.currentOrder.updateOrderRequirements = requirements;
    } else {
      if (requirements.missingProducts === hasMissingProducts && requirements.supportToDropOff === true) {
        return;
      }

      requirements.missingProducts = hasMissingProducts;
      requirements.supportToDropOff = true;
    }

    this.orderService.patchUpdateOrderRequirements(
      this.currentOrder.orderNumber,
      this.currentOrder.zone,
      requirements,
    );
  }

  async updateOrder(): Promise<Order[]> {
    this.syncPreparedQuantitiesWithSupports();

    const res = await this.orderService.updateOrderProcessedQuantities(
      this.currentOrder.orderNumber,
      this.currentOrder.zone,
      this.currentOrder.products
    );

    if (res == null || res.length === 0) {
      throw new Error('Change order state failed.');
    }

    this.currentOrder = res.find(order => order.orderId === this.currentOrder.orderId);

    this.processUpdateOrderRequirements();

    this.canGoBack = true;

    return res;
  }

  syncPreparedQuantitiesWithSupports(): void {
    (this.currentOrder?.products ?? []).forEach(product => {
      const orderedQty = product.quantity?.value ?? 0;
      const totalPrepared = this.getTotalPreparedQuantity(product.cug);
      product.processedQuantity = new Quantity(Math.min(totalPrepared, orderedQty), product.quantity?.unit);
    });
  }

  async sendOrderToTalend(): Promise<void> {
    const talendRequest = this.convertSupportsToConfirmOrderPreparationRequest();
    if (!talendRequest.products.length) return;
    await this.supportService.confirmPreparationSupports(talendRequest);
  }

  convertSupportsToConfirmOrderPreparationRequest(): ConfirmOrderPreparationRequest {
    const talendRequest: ConfirmOrderPreparationRequest = new ConfirmOrderPreparationRequest();
    talendRequest.orderNumber = this.currentOrder.orderNumber;
    talendRequest.driveId = this.currentOrder.driveId;
    talendRequest.storeId = this.currentOrder.storeId;
    talendRequest.type = this.currentOrder.type;
    talendRequest.deliverySiteId = this.currentOrder.deliverySiteId;
    talendRequest.products = this.supports.flatMap(support => support.products.filter(product => product.measures != null));
    return talendRequest;
  }

  closeRetryDialog(): void {
    this.isLoadingDialogOpen = false;
  }

  startCreateSupport(): void {
    this.currentSupportCode = null;
    this.selectedSupport = null;
    this.focusInput();
  }

  cleanEmptySupports(): void {
    this.supports = [ ...this.supports ]
      .filter(s => s.products != null && s.products.length > 0);
  }

  updateProcessedQuantity(modifiedQuantity: ModifiedQuantity, product: OrderProduct): void {
    product.processedQuantity = modifiedQuantity.quantity;
  }

  updateFoundQuantity(modifiedQuantity: ModifiedQuantity, product: OrderProduct): void {
    this.foundQuantities.set(product.cug, modifiedQuantity.quantity);
  }

  mergeFoundQuantity(product: OrderProduct): void {
    const foundQuantity = this.foundQuantities.get(product.cug);

    if (foundQuantity == null) {
      return;
    }

    const alreadyPrepared = product.processedQuantity?.value ?? 0;
    const orderedQty = product.quantity?.value ?? 0;
    const newTotal = Math.min(alreadyPrepared + (foundQuantity.value ?? 0), orderedQty);

    product.processedQuantity = new Quantity(newTotal, product.quantity?.unit);
    this.foundQuantities.delete(product.cug);
  }

  onSubstitute(event: { product: OrderProduct }): void {
    this.currentProduct = event.product;
    this.isSubstitutionDialogOpen = true;
  }

  confirmSubstitution(): void {
    if (this.currentProduct == null) {
      return;
    }

    this.currentProduct.isSubstituted = true;
    this.closeSubstitutionDialog();
  }

  cancelSubstitution(): void {
    this.closeSubstitutionDialog();
  }

  closeSubstitutionDialog(): void {
    this.currentProduct = null;
    this.isSubstitutionDialogOpen = false;
  }

  onProductAction(event: { product: OrderProduct }): void {
    this.currentProduct = event?.product;

    if (this.currentProduct == null){
      return;
    }

    this.ensureSelectedSupportProducts();

    if (this.currentProduct.isScannable) {
      this.currentSupportProduct = this.supports
        .flatMap(support => support.products)
        .find(supportProduct => supportProduct.cug === this.currentProduct.cug);
      return;
    }

    this.mergeFoundQuantity(this.currentProduct);

    const processedQty = this.currentProduct.processedQuantity?.value ?? 0;

    if (processedQty < this.currentProduct.quantity.value) {
      this.handleOutOfStock({product: this.currentProduct});
      return;
    }

    const supportProduct = this.productConverter.convertOrderProductToSupportProduct(this.currentProduct);
    this.allocateSupportProductQuantity(supportProduct, this.currentProduct.quantity.value);
    this.moveProductToSelectedSupport(supportProduct);
    this.persistSupportProducts([this.currentProduct]);
  }

  onForceOutOfStock(event: OutOfStockPayload): void {
    this.forceOutOfStock = true;
    this.handleOutOfStock(event);
  }

  handleOutOfStock({ product, supportProduct }: OutOfStockPayload): void {
    this.outOfStockProposition = product.label;
    this.currentProduct = product;
    this.isProcessedCommandDialogOpenWithOutOfStock = true;

    if (supportProduct != null) {
      this.currentSupportProduct = supportProduct;
    }
  }

  ensureSelectedSupportProducts(): void {
    if (this.selectedSupport != null && this.selectedSupport.products == null) {
      this.selectedSupport.products = [];
    }
  }

  outOfStockAndProcessed(): void {
    this.resetPreparedQuantityIfForced();
    this.applyToSelectedProduct( p => this.applyOutOfStock(p));
  }

  continueWithoutProduct(): void {
    this.resetPreparedQuantityIfForced();
    this.applyToSelectedProduct(p => this.applyProductPreparation(p));
  }

  resetPreparedQuantityIfForced(): void {
    if (this.forceOutOfStock && this.currentProduct != null) {
      this.currentProduct.processedQuantity.value = 0;
    }
  }

  private applyToSelectedProduct(action: (product: OrderProduct) => void): void {
    this.canGoBack = false;
    if (this.currentProduct != null) {
      action(this.currentProduct);
    }
    this.closeDialogOutOfStock();
  }

  applyOutOfStock(product: OrderProduct): void {
    this.applyProductPreparation(product);

    this.productService
      .updateProductOutOfStockStatus(this.currentOrder.zone, product.cug)
      .then(() => {
        EventBus.$emit(EventBus.UPDATE_PRODUCT_OUT_OF_STOCK_STATUS, {});
      });
    this.persistSupportProducts([product]);
  }

  closeDialogOutOfStock(): void {
    this.outOfStockProposition = '';
    this.isProcessedCommandDialogOpenWithOutOfStock = false;
    this.forceOutOfStock = false;
    this.clearCurrentProduct();
  }

  moveProductToSelectedSupport(product: SupportProduct): void {
    if (!this.selectedSupport) return;

    const products = this.selectedSupport.products ?? [];
    const index = products.findIndex(
      existingProduct => existingProduct.cug === product.cug
    );

    if ((product.processedQuantity ?? 0) <= 0) {
      if (index !== -1) {
        products.splice(index, 1);
      }
    } else if (index !== -1) {
      products.splice(index, 1, product);
    } else {
      products.push(product);
    }

    this.selectedSupport.products = products;
    this.orphanProducts = this.orphanProducts.filter(p => p.cug !== product.cug);
    if (this.missingSupport?.products?.some(p => p.cug === product.cug)) {
      if(this.currentProduct?.processedQuantity?.value === this.currentProduct?.quantity.value) {
        this.outOfStockProducts = this.outOfStockProducts.filter(p => p.cug !== product.cug);
        this.removeProductFromMissingSupport(this.currentProduct);
      }
    }
    this.exitSelectedSupportEditMode();
  }

  persistSupportProducts(products: OrderProduct[]): void {
    const hasProducts = products.some(product => product.processedQuantity.value > 0);

    if (this.selectedSupport == null) {
      return;
    }

    const productsToUpdate: SupportProduct[] = products.map(product => this.productConverter.convertOrderProductToSupportProduct(product));

    if (!hasProducts) {
      if (this.selectedSupport.id != null) {
        this.persistQueue = this.persistQueue.then(() =>
          this.supportService.updateSupportProducts(this.selectedSupport, productsToUpdate)
        );
      }
      return;
    }

    if (this.selectedSupport.id == null) {
      this.persistQueue = this.persistQueue.then(() =>
        this.supportService.createSupport(this.createSupportRequestFromSupport(this.selectedSupport))
          .then(createdSupport => {
            this.selectedSupport = createdSupport;

            const index = this.supports.findIndex(
              support => support.code === createdSupport.code
            );

            if (index !== -1) {
              this.supports[index] = createdSupport;
            }
          })
      );
    } else {
      this.persistQueue = this.persistQueue.then(() =>
        this.supportService.updateSupportProducts(this.selectedSupport, productsToUpdate)
      );
    }
  }

  exitSelectedSupportEditMode(): void {
    const selectedSupportPanel = this.$refs.selectedSupportPanel as { exitEditMode?: () => void } | undefined;
    selectedSupportPanel?.exitEditMode?.();
  }

  onProductPreparationComplete(supportProduct: SupportProduct): void {
    this.currentSupportProduct = supportProduct;
    this.applyProductPreparation(this.currentProduct);
    this.persistSupportProducts([this.currentProduct]);
    this.clearCurrentProduct();
  }

  onSupportDelete(event: { supportToDelete: Support | null | undefined }): void {
    this.isSupportEdited = true;
    const supportToDelete = event?.supportToDelete;
    if (!supportToDelete) return;

    this.removeProductsFromSupport(supportToDelete);

    if (supportToDelete.code === this.currentSupportCode) {
      this.startCreateSupport();
    }

    this.supports = (this.supports ?? []).filter(
      support => support.code !== supportToDelete.code
    );

    if (supportToDelete.id != null) {
      this.supportService.deleteSupport(supportToDelete.id);
    }
  }

  private removeProductsFromSupport(supportToDelete: Support) {
    (supportToDelete.products ?? []).forEach(product => {
      const orderProduct = this.currentOrder?.products?.find(
        item => item.cug === product.cug
      );

      if (!orderProduct) {
        return;
      }

      const remainingPreparedQuantity = this.getTotalPreparedQuantity(orderProduct.cug, supportToDelete);
      orderProduct.processedQuantity = new Quantity(remainingPreparedQuantity, orderProduct.quantity?.unit);
      orderProduct.status = OrderedProductStatuses.TO_PREPARE;
      if (this.missingSupport?.products?.some(p => p.cug === orderProduct.cug)) {
        this.removeFromOutOfStock(orderProduct);
      }

      if (!this.orphanProducts.some(p => p.cug === orderProduct.cug)) {
        this.orphanProducts.push(orderProduct);
      }
    });
  }

  onSupportAction(event: {products: OrderProduct[]}): void {
    this.isSupportEdited = true;
    this.isApplyingSupportEdit = true;
    try {
      event.products.forEach(product => this.applyEditedSupportProduct(product));
    } finally {
      this.isApplyingSupportEdit = false;
    }
    this.persistSupportProducts(event.products);
  }

  applyEditedSupportProduct(product: OrderProduct): void {
    if (product == null || product.isScannable) {
      return;
    }

    const orderedQty = product.quantity?.value ?? 0;
    const totalPrepared = this.getTotalPreparedQuantity(product.cug);
    product.processedQuantity = new Quantity(Math.min(totalPrepared, orderedQty), product.quantity?.unit);

    this.currentProduct = product;
    this.currentSupportProduct = null;
    this.applyProductPreparation(product);
    this.clearCurrentProduct();
  }

  getTotalPreparedQuantity(cug: string, excludeSupport: Support | null = null): number {
    return this.supports
      .filter(support => support !== excludeSupport)
      .flatMap(support => support.products ?? [])
      .filter(supportProduct => supportProduct.cug === cug)
      .reduce((sum, supportProduct) => sum + (supportProduct.processedQuantity ?? 0), 0);
  }

  applyProductPreparation(product: OrderProduct): void {
    product.status = OrderedProductStatuses.PREPARED;

    const processedQty = product.processedQuantity?.value ?? 0;
    const orderedQty = product.quantity?.value ?? 0;

    if (processedQty === 0) {
      this.moveToOutOfStock(product);
      this.removeEditingProductFromCurrentSupport(product);
      return;
    }

    if (processedQty < orderedQty) {
      this.moveToOutOfStock(product);
    } else {
      this.removeFromOutOfStock(product);
    }

    const isNewAllocation = this.currentSupportProduct == null;
    const supportProduct =
      this.currentSupportProduct ??
      this.productConverter.convertOrderProductToSupportProduct(product);
    if (isNewAllocation) {
      this.allocateSupportProductQuantity(supportProduct, orderedQty);
    }
    if (!this.isApplyingSupportEdit) {
      this.moveProductToSelectedSupport(supportProduct);
      this.persistSupportProducts([product]);
    }
  }

  allocateSupportProductQuantity(supportProduct: SupportProduct, orderedQty: number): void {
    if (!this.selectedSupport) {
      return;
    }

    const otherSupportsQty = this.getTotalPreparedQuantity(supportProduct.cug, this.selectedSupport);
    supportProduct.processedQuantity = Math.max(0, Math.min(supportProduct.processedQuantity, orderedQty) - otherSupportsQty);
  }

  removeFromOutOfStock(product: OrderProduct): void {
    this.outOfStockProducts = this.outOfStockProducts.filter(
      outOfStockProduct => outOfStockProduct.cug !== product.cug
    );
    this.removeProductFromMissingSupport(product);
  }

  removeProductFromMissingSupport(product: OrderProduct): void {
    if(this.missingSupport?.products != null) {
      const supportProduct: SupportProduct = this.missingSupport.products.find(supportProduct => supportProduct.cug === product.cug);
      if (supportProduct == null) {
        return;
      }
      supportProduct.missingQuantity = product.quantity.value - (product.processedQuantity?.value ?? 0);
      if(this.outOfStockProducts.length === 0) {
        this.supportService.deleteSupport(this.missingSupport.id);
        this.missingSupport = null;
      } else {
        this.supportService.updateSupportProducts(this.missingSupport, this.missingSupport.products);
        this.missingSupport.products = this.missingSupport.products.filter(supportProduct => supportProduct.cug != product.cug);
      }
      this.isMissingSupportEdited = true;
    }
  }

  addProductToMissingSupport(product: OrderProduct): void {
    const supportProduct: SupportProduct = this.productConverter.convertOrderProductToSupportProduct(product);
    supportProduct.missingQuantity = product.quantity.value - (product.processedQuantity?.value ?? 0);
    if(!this.missingSupport) {
      const newMissingSupport = this.createSupport();
      newMissingSupport.code = "MISSING";
      newMissingSupport.products= [supportProduct];
      this.supportService.createSupport(this.createSupportRequestFromSupport(newMissingSupport)).then(support => {
        this.missingSupport = support;
      });
    } else {
      if (this.missingSupport.products == null) {
        this.missingSupport.products = [];
      }
      const existingProduct = this.missingSupport.products.find(p => p.cug === product.cug);
      if (existingProduct != null) {
        existingProduct.missingQuantity = supportProduct.missingQuantity;
        existingProduct.processedQuantity = supportProduct.processedQuantity;
      } else {
        this.missingSupport.products.push(supportProduct);
      }
      this.supportService.updateSupportProducts(this.missingSupport, this.missingSupport.products);
    }
  }

  removeEditingProductFromCurrentSupport(product: OrderProduct): void {
    const products = this.selectedSupport?.products;

    if (!product || !products) {
      return;
    }

    this.selectedSupport.products = products.filter(
      supportProduct => supportProduct?.cug !== product.cug
    );
  }

  moveToOutOfStock(product: OrderProduct): void {
    this.outOfStockProducts = this.outOfStockProducts.some(p => p.cug === product.cug)
      ? this.outOfStockProducts
      : [...this.outOfStockProducts, product];

    this.orphanProducts = this.orphanProducts.filter(p => p.cug !== product.cug);
    this.persistSupportProducts([product]);
    this.addProductToMissingSupport(product);
  }

  onProductPreparationCancel(): void {
    this.clearCurrentProduct();
  }

  canShowQuantityOutput(product: OrderProduct): boolean {
    return this.hasSelectedSupport && !product.isScannable;
  }

  focusInput() {
    this.$nextTick(() => {
      (this.$refs.supportInput as HTMLInputElement)?.focus();
    });
  }

  onScanInput(value: string): void {
    this.scanDetector.onInput(value);
  }

  closeErrorPopUp(): void {
    this.hasScanError = false;
    this.errorMessage = '';
    this.currentSupportCode = '';
    this.focusInput();
  }

  clearCurrentProduct(): void {
    this.currentProduct = null;
    this.currentSupportProduct = null;
  }

  toggleOutOfStockDisplay(): void {
    this.displayOutOtStockProducts = !this.displayOutOtStockProducts;
  }

  get existingMeasures(): Quantity[] {
    return this.supports?.filter(support => support?.code != this.selectedSupport?.code)
      .flatMap(support => support.products)
      .filter(product => product.cug === this.currentProduct?.cug)
      .flatMap(product => product.measures);
  }

  get canEditSupport(): boolean {
    return this.selectedSupport?.supportStatus != SupportStatuses.DROPPED_OFF;
  }

  get hasValidCurrentSupportCode(): boolean {
    return this.REGEX_SUPPORT_CODE.test(
      this.currentSupportCode?.trim() ?? ''
    );
  }

  get hasSelectedSupport(): boolean {
    return this.selectedSupport != null;
  }

  get canAddNewSupport(): boolean {
    return this.hasOutOfStockProducts || this.hasOrphanProducts;
  }

  get canScanSupport(): boolean {
    return !this.hasSelectedSupport;
  }

  get canCreateSupport(): boolean {
    return this.hasValidCurrentSupportCode;
  }

  get displayableSupports(): Support[] {
    if(this.supports == null || this.supports.length === 0) {
      return [];
    }
    return this.supports
      .filter(s => !this.hasSelectedSupport || this.selectedSupport.code !== s.code)
      .sort((a, b) => a.code.localeCompare(b.code));
  }

  get selectedSupportCode(): string {
    return this.selectedSupport?.code ?? '';
  }

  get hasOrphanProducts(): boolean {
    return this.orphanProducts?.length > 0;
  }

  get hasOutOfStockProducts(): boolean {
    return this.missingSupport && this.missingSupport?.products?.length > 0;
  }

  get hasMultipleOutOfStockProducts(): boolean {
    return this.missingSupport?.products?.length > 1;
  }

  get canEndOrderPreparation(): boolean {
    return !this.hasOrphanProducts && !this.isOrderProcessed;
  }

  get isSupportsEdited(): boolean {
    return this.isMissingSupportEdited || this.isSupportEdited;
  }

  get isOrderProcessed(): boolean {
    return this.currentOrder.status.toString() === OrderState[OrderState.PROCESSED]
  }
}

type OutOfStockPayload = {
  product: OrderProduct;
  supportProduct?: SupportProduct;
};

enum PreparationStep {
  SUPPORT = 'SUPPORT',
  ORDER = 'ORDER',
  TALEND = 'TALEND',
  DONE = 'DONE'
}
